/* Client-side asset trails.
   Keeps a sliding history of lat/lng per moving asset (flights, ships, fleet)
   across polling cycles so the globe can draw "threads" behind each one.
   The store is module-local; points come in through repeated calls. */
import { useMemo, useRef } from "react"
import type { GlobePoint } from "./types"

export type TrailKind = "flight" | "ship" | "fleet"

/** Flattened trail entry returned from the hook, ready to hand to a render layer. */
export interface TrailItem {
  id: string
  kind: TrailKind
  owned: boolean
  color: string
  /** Ordered oldest→newest, each [lat, lng]. At least one entry. */
  coords: Array<[number, number]>
}

interface StoreEntry {
  kind: TrailKind
  owned: boolean
  color: string
  /** [lat, lng, timestampMs] triples, oldest first. */
  coords: Array<[number, number, number]>
  lastSeenMs: number
}

const MAX_POINTS_PER_TRAIL = 60
const MIN_MOVE_DEG = 0.01
const MIN_APPEND_INTERVAL_MS = 10_000
const PRUNE_AFTER_MS = 10 * 60 * 1000

function isTrailKind(k: GlobePoint["kind"]): k is TrailKind {
  return k === "flight" || k === "ship" || k === "fleet"
}

/**
 * Accumulate per-asset trails over repeated polling cycles.
 *
 * Stores history in a ref-backed Map so identity survives renders without
 * triggering extra re-renders. Each call:
 *   - ensures every moving point has an entry (seeded if new)
 *   - appends a fresh coord only if the asset has moved > MIN_MOVE_DEG or
 *     > MIN_APPEND_INTERVAL_MS since the last append
 *   - prunes trails untouched for PRUNE_AFTER_MS
 *   - caps each trail at MAX_POINTS_PER_TRAIL
 */
export function useAssetTrails(points: GlobePoint[]): TrailItem[] {
  const storeRef = useRef<Map<string, StoreEntry>>(new Map())
  const getTrailRef = useRef<(id: string) => TrailItem | null>(() => null)

  const trails = useMemo<TrailItem[]>(() => {
    const store = storeRef.current
    const nowMs = Date.now()

    for (const p of points) {
      if (!isTrailKind(p.kind)) continue
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) continue

      const existing = store.get(p.id)
      if (!existing) {
        // Seed with a single coord; the trail will show as a dot for one
        // cycle and then extend once the asset moves.
        store.set(p.id, {
          kind: p.kind,
          owned: !!p.owned,
          color: p.color,
          coords: [[p.lat, p.lng, nowMs]],
          lastSeenMs: nowMs,
        })
        continue
      }

      existing.kind = p.kind
      existing.owned = !!p.owned
      existing.color = p.color
      existing.lastSeenMs = nowMs

      const last = existing.coords[existing.coords.length - 1]
      const shouldAppend = (() => {
        if (!last) return true
        const dLat = Math.abs(p.lat - last[0])
        const dLng = Math.abs(p.lng - last[1])
        const moved = dLat > MIN_MOVE_DEG || dLng > MIN_MOVE_DEG
        const stale = nowMs - last[2] > MIN_APPEND_INTERVAL_MS
        return moved || stale
      })()

      if (shouldAppend) {
        existing.coords.push([p.lat, p.lng, nowMs])
        if (existing.coords.length > MAX_POINTS_PER_TRAIL) {
          existing.coords.splice(0, existing.coords.length - MAX_POINTS_PER_TRAIL)
        }
      }
    }

    // Prune entries not updated in a while. Use a tombstone pass so we don't
    // mutate the Map while iterating.
    const cutoff = nowMs - PRUNE_AFTER_MS
    const doomed: string[] = []
    for (const [id, entry] of store.entries()) {
      if (entry.lastSeenMs < cutoff) doomed.push(id)
    }
    for (const id of doomed) store.delete(id)

    // Snapshot: stable per-call array of plain objects for downstream renderers.
    const out: TrailItem[] = []
    for (const [id, entry] of store.entries()) {
      out.push({
        id,
        kind: entry.kind,
        owned: entry.owned,
        color: entry.color,
        coords: entry.coords.map<[number, number]>((c) => [c[0], c[1]]),
      })
    }
    return out
  }, [points])

  getTrailRef.current = (id: string) => {
    const entry = storeRef.current.get(id)
    if (!entry) return null
    return {
      id,
      kind: entry.kind,
      owned: entry.owned,
      color: entry.color,
      coords: entry.coords.map<[number, number]>((c) => [c[0], c[1]]),
    }
  }

  return trails
}
